/**
 * Lightbox Gallery Overlay with Clean Edge-to-Edge Full-screen Sliding Track & GPU Acceleration
 * Features:
 * - Clean Edge-to-Edge View (No side arrow buttons cluttering the photo area)
 * - Full-screen Sliding Track that moves in real-time with finger touch (60 FPS GPU)
 * - Seamless Infinite Looping Navigation via Touch Swipe, Thumbnails Strip, and Keyboard Arrow Keys
 * - 2-finger Pinch-to-Zoom (up to 4x) on active photo
 * - Pan / Drag zoomed photos in 360 degrees
 * - Double-tap to toggle zoom (1x <-> 2.5x)
 */

export interface LightboxImage {
  url: string;
  thumbUrl?: string;
  path?: string;
}

export function openLightbox(images: LightboxImage[], initialIndex: number = 0): void {
  if (!images || images.length === 0) return;

  const totalCount = images.length;
  const isInfinite = totalCount > 1;
  const initialTrackIndex = isInfinite ? Math.max(0, Math.min(initialIndex, totalCount - 1)) + 1 : 0;


  // Remove existing lightbox if present
  const existing = document.querySelector('.lightbox-overlay');
  if (existing) {
    existing.remove();
  }

  const overlay = document.createElement('div');
  overlay.className = 'lightbox-overlay';

  // Build slides with edge clones if multi-image
  const trackItems = isInfinite
    ? [
        { ...images[totalCount - 1], isClone: true, realIndex: totalCount - 1 },
        ...images.map((img, i) => ({ ...img, isClone: false, realIndex: i })),
        { ...images[0], isClone: true, realIndex: 0 },
      ]
    : [{ ...images[0], isClone: false, realIndex: 0 }];

  const initialRealIndex = isInfinite ? initialTrackIndex - 1 : 0;

  overlay.innerHTML = `
    <div class="lightbox-top-bar">
      <span class="lightbox-counter"><span id="lb-current">${initialRealIndex + 1}</span> / ${totalCount}</span>
      <button class="lightbox-close" aria-label="Close">✕</button>
    </div>

    <div class="lightbox-content">
      <div class="lightbox-stage">
        <div class="lightbox-track">
          ${trackItems
            .map(
              (img, i) => `
            <div class="lightbox-slide" data-track-index="${i}" data-real-index="${img.realIndex}">
              <img class="lightbox-slide-img" src="${img.url}" alt="Room Image ${img.realIndex + 1}" draggable="false" />
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    </div>

    ${
      totalCount > 1
        ? `
      <div class="lightbox-thumbs">
        ${images
          .map(
            (img, idx) => `
          <button class="lightbox-thumb-item ${idx === initialRealIndex ? 'active' : ''}" data-index="${idx}">
            <img src="${img.thumbUrl || img.url}" alt="thumb ${idx + 1}" draggable="false" />
          </button>
        `
          )
          .join('')}
      </div>
    `
        : ''
    }
  `;

  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';

  const trackEl = overlay.querySelector('.lightbox-track') as HTMLElement;
  const items = Array.from(overlay.querySelectorAll('.lightbox-slide')) as HTMLElement[];
  const currentEl = overlay.querySelector('#lb-current') as HTMLElement;
  const closeBtn = overlay.querySelector('.lightbox-close') as HTMLElement;
  const thumbBtns = Array.from(overlay.querySelectorAll('.lightbox-thumb-item')) as HTMLElement[];
  const thumbsContainer = overlay.querySelector('.lightbox-thumbs') as HTMLElement | null;

  const centerThumbnail = (index: number, smooth: boolean = true) => {
    if (!thumbsContainer) return;
    const activeThumb = thumbBtns[index];
    if (!activeThumb) return;
    const targetScrollLeft = activeThumb.offsetLeft + activeThumb.offsetWidth / 2 - thumbsContainer.clientWidth / 2;
    thumbsContainer.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: smooth ? 'smooth' : 'auto',
    });
  };

  // Position at initial image
  requestAnimationFrame(() => {
    const targetEl = items[initialTrackIndex];
    if (targetEl) {
      trackEl.scrollLeft = targetEl.offsetLeft;
    }
    centerThumbnail(initialRealIndex, false);
  });

  let isTeleporting = false;
  let isTouching = false;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  let lastReportedIndex = initialRealIndex;

  // Silent instant teleport: DOES NOT touch scrollSnapType (Zero Composite Invalidation)
  // Jumps to the target snap point with behavior: 'instant' using exact DOM offsetLeft
  const silentTeleport = (targetIndex: number) => {
    const targetEl = items[targetIndex];
    if (!targetEl) return;
    isTeleporting = true;
    trackEl.scrollTo({ left: targetEl.offsetLeft, behavior: 'instant' as ScrollBehavior });
    setTimeout(() => {
      isTeleporting = false;
    }, 50);
  };

  // Check if we have rested on a clone and need to teleport
  const onScrollRest = () => {
    if (isTeleporting || isTouching || !isInfinite) return;
    const scrollLeft = trackEl.scrollLeft;

    const cloneFirstEl = items[totalCount + 1]; // Clone of Image 0 at right boundary
    const cloneLastEl = items[0];               // Clone of Image N-1 at left boundary
    if (!cloneFirstEl || !cloneLastEl) return;

    if (Math.abs(scrollLeft - cloneFirstEl.offsetLeft) < 15) {
      silentTeleport(1);
      updateDisplayInfo(0, true);
    } else if (Math.abs(scrollLeft - cloneLastEl.offsetLeft) < 15) {
      silentTeleport(totalCount);
      updateDisplayInfo(totalCount - 1, true);
    }
  };

  let currentScale = 1;
  let currentPanX = 0;
  let currentPanY = 0;
  let isPanning = false;
  let isPinching = false;
  let lastTapTime = 0;
  let startPinchDistance = 0;
  let startScale = 1;
  let panStartX = 0;
  let panStartY = 0;
  let initialPanX = 0;
  let initialPanY = 0;

  const getActiveImage = (): HTMLElement | null => {
    const scrollLeft = trackEl.scrollLeft;
    let closestIndex = 0;
    let minDiff = Infinity;
    items.forEach((item, idx) => {
      const diff = Math.abs(scrollLeft - item.offsetLeft);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = idx;
      }
    });
    return items[closestIndex]?.querySelector('.lightbox-slide-img') as HTMLElement | null;
  };

  const applyZoom = (img: HTMLElement, animate: boolean = false) => {
    if (animate) {
      img.style.transition = 'transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)';
    } else {
      img.style.transition = 'none';
    }
    img.style.transform = `translate3d(${currentPanX}px, ${currentPanY}px, 0px) scale(${currentScale})`;
  };

  const resetZoom = (img?: HTMLElement | null, animate: boolean = true) => {
    currentScale = 1;
    currentPanX = 0;
    currentPanY = 0;
    isPanning = false;
    isPinching = false;
    trackEl.style.overflowX = 'auto';

    const targetImg =
      img ||
      (overlay.querySelector('.lightbox-slide-img[style*="scale"]') as HTMLElement | null) ||
      getActiveImage();
    if (targetImg) {
      if (animate) {
        targetImg.style.transition = 'transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)';
      } else {
        targetImg.style.transition = 'none';
      }
      targetImg.style.transform = 'translate3d(0px, 0px, 0px) scale(1)';
    }
  };

  // Update active dot indicator / thumbnail / counter
  const updateDisplayInfo = (explicitRealIdx?: number, isWrapTeleport: boolean = false) => {
    let realIndex = 0;
    if (explicitRealIdx !== undefined) {
      realIndex = explicitRealIdx;
    } else {
      const scrollLeft = trackEl.scrollLeft;
      let closestIndex = 1;
      let minDiff = Infinity;
      items.forEach((item, idx) => {
        const diff = Math.abs(scrollLeft - item.offsetLeft);
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = idx;
        }
      });
      if (closestIndex <= 0) realIndex = totalCount - 1;
      else if (closestIndex >= totalCount + 1) realIndex = 0;
      else realIndex = closestIndex - 1;
    }

    if (currentEl) {
      currentEl.textContent = (realIndex + 1).toString();
    }
    thumbBtns.forEach((btn, i) => {
      btn.classList.toggle('active', i === realIndex);
    });

    if (isWrapTeleport) {
      lastReportedIndex = realIndex;
      centerThumbnail(realIndex, false);
      resetZoom(null, false);
    } else if (realIndex !== lastReportedIndex) {
      lastReportedIndex = realIndex;
      centerThumbnail(realIndex, true);
      resetZoom(null, false);
    }
  };

  // Modern Web Standard: scrollend event
  trackEl.addEventListener('scrollend', () => {
    if (fallbackTimer) clearTimeout(fallbackTimer);
    onScrollRest();
  });

  // Scroll listener with real-time indicators + fallback timer
  trackEl.addEventListener(
    'scroll',
    () => {
      if (isTeleporting) return;
      updateDisplayInfo();

      if (fallbackTimer) clearTimeout(fallbackTimer);
      fallbackTimer = setTimeout(() => {
        onScrollRest();
      }, 70);
    },
    { passive: true }
  );

  // Touch tracking for double-tap zoom, pinch-to-zoom, and panning
  trackEl.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      // 2 fingers -> Pinch zoom
      if (e.touches.length === 2) {
        const p1 = e.touches[0];
        const p2 = e.touches[1];
        startPinchDistance = Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY);
        startScale = currentScale;
        isPinching = true;
        isPanning = false;
        trackEl.style.overflowX = 'hidden';
        return;
      }

      // 1 finger
      if (e.touches.length === 1) {
        isTouching = true;
        const now = Date.now();
        const touch = e.touches[0];
        const targetImg = (e.target as HTMLElement).closest('.lightbox-slide-img') as HTMLElement | null;

        // Double-tap detection (< 300ms)
        if (targetImg && now - lastTapTime < 300) {
          e.preventDefault();
          lastTapTime = 0;

          if (currentScale > 1.2) {
            resetZoom(targetImg, true);
          } else {
            currentScale = 2.5;
            currentPanX = 0;
            currentPanY = 0;
            trackEl.style.overflowX = 'hidden';
            applyZoom(targetImg, true);
          }
          return;
        }
        lastTapTime = now;

        // If currently zoomed, start panning
        if (currentScale > 1.05) {
          isPanning = true;
          panStartX = touch.clientX;
          panStartY = touch.clientY;
          initialPanX = currentPanX;
          initialPanY = currentPanY;
        }
      }
    },
    { passive: false }
  );

  trackEl.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      // 1. Pinch zoom (2 fingers)
      if (isPinching && e.touches.length === 2) {
        e.preventDefault();
        const p1 = e.touches[0];
        const p2 = e.touches[1];
        const dist = Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY);
        if (startPinchDistance > 0) {
          currentScale = Math.max(1, Math.min(startScale * (dist / startPinchDistance), 4));
          const activeImg = getActiveImage();
          if (activeImg) {
            applyZoom(activeImg, false);
          }
        }
        return;
      }

      // 2. Pan zoomed photo (1 finger)
      if (isPanning && e.touches.length === 1 && currentScale > 1.05) {
        e.preventDefault();
        const touch = e.touches[0];
        const deltaX = touch.clientX - panStartX;
        const deltaY = touch.clientY - panStartY;

        const maxPanX = (window.innerWidth * (currentScale - 1)) / 2;
        const maxPanY = (window.innerHeight * (currentScale - 1)) / 2;

        currentPanX = Math.max(-maxPanX, Math.min(maxPanX, initialPanX + deltaX));
        currentPanY = Math.max(-maxPanY, Math.min(maxPanY, initialPanY + deltaY));

        const activeImg = getActiveImage();
        if (activeImg) {
          applyZoom(activeImg, false);
        }
      }
    },
    { passive: false }
  );

  const onTouchEnd = (e: TouchEvent) => {
    if (isPinching && e.touches.length < 2) {
      isPinching = false;
      if (currentScale < 1.05) {
        resetZoom(getActiveImage(), true);
      } else {
        trackEl.style.overflowX = 'hidden';
      }
    }

    if (e.touches.length === 0) {
      isTouching = false;
      isPanning = false;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      fallbackTimer = setTimeout(() => {
        onScrollRest();
      }, 80);
    }
  };
  trackEl.addEventListener('touchend', onTouchEnd, { passive: true });
  trackEl.addEventListener('touchcancel', onTouchEnd, { passive: true });

  // Desktop double-click to zoom
  trackEl.addEventListener('dblclick', (e) => {
    const targetImg = (e.target as HTMLElement).closest('.lightbox-slide-img') as HTMLElement | null;
    if (!targetImg) return;
    if (currentScale > 1.2) {
      resetZoom(targetImg, true);
    } else {
      currentScale = 2.5;
      currentPanX = 0;
      currentPanY = 0;
      trackEl.style.overflowX = 'hidden';
      applyZoom(targetImg, true);
    }
  });

  // Thumbnail clicks
  thumbBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (isTeleporting) return;
      resetZoom(null, false);
      const idx = parseInt(btn.getAttribute('data-index') || '0', 10);
      const targetEl = items[isInfinite ? idx + 1 : idx];
      if (targetEl) {
        trackEl.scrollTo({ left: targetEl.offsetLeft, behavior: 'smooth' });
      }
    });
  });

  // Next & Prev actions
  const goToNext = () => {
    if (isTeleporting || !isInfinite) return;
    resetZoom(null, false);
    const scrollLeft = trackEl.scrollLeft;
    const currentIdx = items.findIndex(
      (item, idx) => idx >= 1 && idx <= totalCount && Math.abs(scrollLeft - item.offsetLeft) < 20
    );
    if (currentIdx >= totalCount) {
      const cloneFirstEl = items[totalCount + 1];
      if (cloneFirstEl) trackEl.scrollTo({ left: cloneFirstEl.offsetLeft, behavior: 'smooth' });
    } else {
      const nextEl = items[(currentIdx > 0 ? currentIdx : 1) + 1];
      if (nextEl) trackEl.scrollTo({ left: nextEl.offsetLeft, behavior: 'smooth' });
    }
  };

  const goToPrev = () => {
    if (isTeleporting || !isInfinite) return;
    resetZoom(null, false);
    const scrollLeft = trackEl.scrollLeft;
    const currentIdx = items.findIndex(
      (item, idx) => idx >= 1 && idx <= totalCount && Math.abs(scrollLeft - item.offsetLeft) < 20
    );
    if (currentIdx <= 1) {
      const cloneLastEl = items[0];
      if (cloneLastEl) trackEl.scrollTo({ left: cloneLastEl.offsetLeft, behavior: 'smooth' });
    } else {
      const prevEl = items[(currentIdx > 0 ? currentIdx : 1) - 1];
      if (prevEl) trackEl.scrollTo({ left: prevEl.offsetLeft, behavior: 'smooth' });
    }
  };

  // Keyboard navigation for desktop
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    } else if (e.key === 'ArrowLeft') {
      goToPrev();
    }
  };
  window.addEventListener('keydown', handleKeyDown);

  const closeLightbox = () => {
    resetZoom(null, false);
    overlay.classList.add('fade-out');
    window.removeEventListener('keydown', handleKeyDown);
    setTimeout(() => {
      document.body.style.overflow = '';
      overlay.remove();
    }, 200);
  };

  closeBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    closeLightbox();
  });

  overlay.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (
      currentScale === 1 &&
      (target.classList.contains('lightbox-overlay') ||
        target.classList.contains('lightbox-content') ||
        target.classList.contains('lightbox-stage'))
    ) {
      closeLightbox();
    }
  });
}
